import type {
  CardHeaderProps,
  GridProps
} from '@chakra-ui/react'
import {
  Button,
  Flex,
  Grid,
  HStack,
  Stack,
  Text,
} from '@chakra-ui/react'
import {
  type InsuranceParamsDto,
  type InsuranceParamsPropertyDto,
  type InsuranceParamsTravelDto
} from 'BrokerTsLib/src/dtos'
import { Product } from 'BrokerTsLib/src/enums'
import * as global from 'brokertslib/src/helpers/global'
import { FormProvider, useForm, yupResolver } from 'BrokerTsLib/src/rhf'
import {
  PropertyValidationRuleObject,
  TravelAddonValidationSchema,
  TravelValidationBaseObject,
  VehicleBaseParamsValidationSchema,
} from 'BrokerTsLib/src/validation'
import { forwardRef, useEffect, useState } from 'react'
import * as yup from 'yup'

import { Apps } from '@/dtos'
import { useApp } from '@/hooks/useApp'
import { useGetInsuranceParamsDiscounts } from '@/hooks/useInsuranceParams'
import { useGenerateMakeInsuranceOffers } from '@/hooks/useOffer'

import { CustomCollapse } from 'BrokerTsLib/src/components'
import { isIn } from 'BrokerTsLib/src/Utils/comparisonUtils'
import { FormBody } from '../../containers/forms/FormBody'
import { FormSection } from '../../containers/forms/FormSection'
import { ParametersForm as PropertyParametersForm } from '../../containers/steps/property/Step2/ParametersForm'
import { ParametersForm as TravelParametersForm } from '../../containers/steps/travel/Step2/forms/ParametersForm'
import { ParametersForm as ExtraParametersForm } from '../../containers/steps/vehicle/extra/Step2/ParametersForm'
import { ParametersForm as VehicleParametersForm } from '../../containers/steps/vehicle/Step2/forms/ParametersForm'
import type { ICalculationState } from '../../containers/steps/vehicle/Step2/Step2'
import { useInsuranceParams } from '../../context/InsuranceParamsProvider'
import { useSaveBaseParams } from '../../hooks/useWizard'
import ErrorSummary from '../ErrorSummary'

export interface ParametersProps extends GridProps {
  id: string
  calculationState: ICalculationState
  onCalculateOffers?: () => Promise<void>
  innerRef?: React.Ref<HTMLDivElement>
  isCollapsed: boolean
  cardHeaderProps?: CardHeaderProps
}

export const vehicleBaseSchema = yup.object().shape({
  baseParams: VehicleBaseParamsValidationSchema as any,
})

export const travelBaseSchema = yup.object().shape({
  travel: yup.object().shape(TravelValidationBaseObject),
  addons: TravelAddonValidationSchema,
})


export const propertyBaseSchema = yup.object().shape({
  property: yup.object().shape(PropertyValidationRuleObject),
})

export const liabilityBaseSchema = yup.object().shape({
  property: yup.object().shape({
    ...PropertyValidationRuleObject,
    data: yup.object().shape({}),
    type: yup.string().nullable().optional(),
    range: yup.string().nullable().optional(),
  }),
})


const getSchema = (product: Product) => {
  switch (product) {
    case Product.TravelInsurance:
      return travelBaseSchema
    case Product.LiabilityInsurance:
      return liabilityBaseSchema
    case Product.PropertyInsurance:
      return propertyBaseSchema
    default:
      return vehicleBaseSchema
  }
}

export const Parameters = forwardRef(
  (
    {
      id,
      calculationState,
      onCalculateOffers,
      innerRef,
      isCollapsed,
      cardHeaderProps,
      ...rest
    }: ParametersProps,
    ref,
  ) => {
    const { product, app } = useApp()
    const {
      insuranceParamsId,
      insuranceParams,
      refetchInsuranceParams,
      isDisabled,
      isSubaruInsurance,
      isMazdaInsurance,
      isKiaInsurance
    } = useInsuranceParams()
    const { data: discountsData } = useGetInsuranceParamsDiscounts(insuranceParamsId!, {
      enabled: app != Apps.publicPoleas
    })
    const [insurersDiscounts, setInsurersDiscounts] = useState<null | string>(null) // vybrane slevy od pojistitelu
    const { mutateAsync: saveBaseParams, isLoading } = useSaveBaseParams(insuranceParamsId!)

    const methods = useForm<InsuranceParamsDto | InsuranceParamsTravelDto | InsuranceParamsPropertyDto>({
      defaultValues: insuranceParams,
      resolver: yupResolver(getSchema(product!) as any) as any,
    })

    const {
      handleSubmit,
      formState: { errors },
      setError,
      reset
    } = methods

    const handleCalculateClick = () => onCalculateOffers?.()

    const onSubmit = handleSubmit(
      (data: InsuranceParamsDto | InsuranceParamsTravelDto | InsuranceParamsPropertyDto) => {
        return saveBaseParams(data)
          .then(() => {
            reset(data)

            handleCalculateClick()?.then(() => refetchInsuranceParams())
          })
          .catch((err) => {
            if (
              !(
                err.response &&
                err.response.status === 400 &&
                err.response.data
              )
            )
              return

            const data = err.response.data

            // zobrazeni serverove validace
            Object.keys(data).forEach((x: any) => {
              setError(global.toCamelCase(x), { message: data[x].join(' ') })
            })
          })
      },
    )

    const handleEditParamsClick = (
      event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    ) => {
      if (cardHeaderProps?.onClick) {
        cardHeaderProps.onClick(event as any)
      }
    }

    const { mutateAsync: generateMakeInsuranceOffers, isLoading: isLoadingMakeInsurance } = useGenerateMakeInsuranceOffers()
    const resolveMakeInsuranceOffers = async () => {
      const createdOfferIds = await generateMakeInsuranceOffers(insuranceParamsId!)

      if (createdOfferIds.data.length > 0) {
        const ipvData = await refetchInsuranceParams()
        if (ipvData.isSuccess)
          reset({ ...ipvData.data.data, baseParams: { ...ipvData.data.data.baseParams, insurerDiscounts: discountsData!.data } })

        onSubmit()
      }
    }

    // generování značkových pojištění (Subaru, Mazda ,...)
    useEffect(() => {
      if ((isSubaruInsurance || isMazdaInsurance || isKiaInsurance) && discountsData && !isLoadingMakeInsurance)
        resolveMakeInsuranceOffers()
    }, [isSubaruInsurance, isMazdaInsurance, isKiaInsurance, !discountsData])

    return (
      <FormSection
        number={1}
        ref={innerRef}
        sectionTitle={
          <HStack w="100%" justifyContent="space-between">
            <Text>Parametry pojištění</Text>

            {isCollapsed && cardHeaderProps?.onClick && !isDisabled && (
              <Button onClick={handleEditParamsClick}>
                Upravit parametry pojištění
              </Button>
            )}
          </HStack>
        }
        id={id}
        cardHeaderProps={cardHeaderProps}
        bodyProps={{
          sx: {
            pb: {
              base: 8,
              md: isCollapsed ? 0 : 12,
            },
          },
        }}
        footer={
          !isCollapsed && !isDisabled && (
            <Stack alignItems="center" w="100%" gap={8}>
              <Flex sx={{ w: '100%', justifyContent: 'center' }}>
                <Button
                  colorScheme="primary"
                  isLoading={calculationState.isCalculating || isLoading}
                  onClick={onSubmit}
                >
                  Poptat pojištění
                </Button>
              </Flex>
              <ErrorSummary errors={errors} />
            </Stack>
          )
        }
        {...rest}
      >
        <CustomCollapse in={!isCollapsed}>
          <Grid
            sx={{
              gap: 12,
            }}
          >
            <FormProvider {...methods}>
              <form onSubmit={onSubmit}>
                <FormBody>
                  {app == Apps.travel && <TravelParametersForm />}

                  {app == Apps.property && <PropertyParametersForm />}

                  {isIn(app!, Apps.vehicle, Apps.leasing, Apps.publicPoleas) && <VehicleParametersForm />}

                  {app == Apps.extra && <ExtraParametersForm />}
                </FormBody>
              </form>
            </FormProvider>
          </Grid>
        </CustomCollapse>
      </FormSection>
    )
  },
)
